import { CartInfo, ProductInfo } from "@/types";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { TermDetail } from "../../../comatic/lib/types";

interface CartState {
  offerId?: number;
  cartId?: number;
  articles: { item: ProductInfo, qty: number, unitPrice: number }[];
  additionalCosts: {
    VAT?: number;
    VOC?: number;
    shipping?: number;
  }; //VAT, VOC, shipping
  total: number;
  grossTotal: number;
  paymentTerms?: TermDetail;
  shippingMethod: string;
  possibleShippingMethods: { labelTag: string, cost: number, erpValue: string, default: boolean, shippingDays: number, duringOpeningHours?: boolean }[];
  internalReference?: string;
  currentDeliveryAddressId?: number;
  possibleDeliveryAddreses: {
    id: number;
    comaticAddressId?: number;
    name: string;
    addressLine1: string;
    addressLine2?: string;
    zip: string;
    city: string;
    country: string;
  }[];
}

const initialState: CartState = {
  articles: [],
  additionalCosts: {},
  total: 0,
  grossTotal: 0,
  shippingMethod: "postB",
  possibleShippingMethods: [],
  possibleDeliveryAddreses: [],
};

export const cartSlice = createSlice({
  name: 'cart',
  initialState,
  reducers: {
    addToCart: (state: CartState, action: PayloadAction<{ item: ProductInfo, qty: number, unitPrice: number }>) => {
      state.articles = state.articles.concat(action.payload)
    },
    removeFromCart: (state: CartState, action: PayloadAction<{ articleId: number }>) => {
      state.articles = state.articles.filter(article => article.item.articleId !== action.payload.articleId);
    },
    updateQty: (state: CartState, action: PayloadAction<{ articleId: number, newQty: number }>) => {
      const articleIndex = state.articles.findIndex(article => article.item.articleId === action.payload.articleId);
      if (articleIndex !== -1) {
        state.articles[articleIndex].qty = action.payload.newQty;
      }
    },
    updateAmounts: (state: CartState, action: PayloadAction<{ VAT: number, VOC: number, shipping: number }>) => {
      state.additionalCosts.VAT = action.payload.VAT;
      state.additionalCosts.VOC = action.payload.VOC;
      state.additionalCosts.shipping = action.payload.shipping;
      state.total = state.articles.reduce((sum, article) => sum + (article.qty * article.unitPrice), 0);
      state.grossTotal = state.total +
        (state.additionalCosts.VAT || 0) +
        (state.additionalCosts.VOC || 0) +
        (state.additionalCosts.shipping || 0);
    },
    updateRefs: (state: CartState, action: PayloadAction<{ offerId?: number, cartId?: number }>) => {
      if (action.payload.offerId !== undefined) {
        state.offerId = action.payload.offerId;
      }
      if (action.payload.cartId !== undefined) {
        state.cartId = action.payload.cartId;
      }
    },
    clear: (state: CartState) => {
      return initialState;
    },
    loadCart: (state: CartState, action: PayloadAction<CartInfo>) => {
      return action.payload;
    },
    addNewDeliveryAddress: (state: CartState, action: PayloadAction<{
      id: number;
      comaticAddressId?: number;
      name: string;
      addressLine1: string;
      addressLine2?: string;
      zip: string;
      city: string;
      country: string;
    }>) => {
      state.possibleDeliveryAddreses.push(action.payload);
      return state;
    },
    setCurrentDeliveryAddressId: (state: CartState, action: PayloadAction<number>) => {
      state.currentDeliveryAddressId = action.payload;
      return state;
    }
  }
});

export const { addToCart, removeFromCart, updateQty, updateAmounts, updateRefs, clear, loadCart, addNewDeliveryAddress, setCurrentDeliveryAddressId } = cartSlice.actions;
export default cartSlice.reducer;
